export interface FileAttachment {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  thumbnail?: string;
  uploadProgress?: number;
  isUploading?: boolean;
  error?: string;
  lastModified?: number;
  uploadError?: string;
  isLocalOnly?: boolean;
}

// Tipos de suporte humano
export type HumanSupportType =
  | "requiresHumanAnswer"
  | "requiresHumanAction"
  | "requiresHumanApproval";

// Estrutura de pergunta/resposta para feedback
export interface SupportMessage {
  question: string;
  answer?: string;
  media?: MediaType;
  answerMedia?: MediaType[];
}

export interface ChatJessMessage {
  id: string;
  content: string;
  author: "jess" | "human";
  timestamp: Date;
  feedbackId?: string;
  feedbackTitle?: string;
  feedbackDetails?: string;
  hasFeedback?: boolean;
  suggestion?: string;
  responder?: string;
  responseTimestamp?: Date;
  attachments?: FileAttachment[]; //  para anexos
  messageType?: "text" | "file" | "mixed" | "audio"; // Adicionado tipo audio
  tags?: string[]; // Tags da mensagem para categorização
  // Novo campo para tipo de suporte humano (apenas para mensagens feedback_request)
  supportType?: HumanSupportType;
  // Novo campo para nome do usuário (apenas para mensagens user_message)
  userName?: string;
  // Novo campo indicando se o feedback foi completado
  isCompleted?: boolean;
  // Substituindo content por supportMessages para feedback_request
  supportMessages?: SupportMessage[];
  // Step ID from workflow (for human requests)
  stepId?: string;
  // Original team messages from HumanRequestInfo for proper conversation flow
  originalTeamMessages?: Message[];
  feedback?: {
    question?: string;
    responder?: string;
    suggestion?: string;
    questionDetails?: string;
    respondedAt?: number;
    questionId?: string;
  };
  // Novos campos para mensagens de áudio
  audioData?: {
    url: string;
    blob?: Blob;
    base64: string;
    duration: number;
    mimeType: string;
    waveformData?: number[]; // Dados da waveform para visualização
  };
  isAudioMessage?: boolean;
  // Campos alternativos para compatibilidade
  type?: "human" | "jess"; // Alternativa para author
  message?: string; // Alternativa para content
}

// ============= NOVOS TIPOS DE EVENTOS ESPECÍFICOS =============

// Mensagem recebida da Jess
export interface MessageFromJessEvent {
  type: "mensagem_ia";
  timestamp: string;
  data: {
    chatId: string;
    perguntaId: string;
    texto: string;
    requerFeedback: boolean;
    tags?: string[];
  };
}

// Usuário responde a Jess
export interface RespostaParaJessEvent {
  type: "resposta_para_jess";
  timestamp: string;
  data: {
    chatId: string;
    perguntaId: string;
    usuarioId: string;
    texto: string;
    origem?: "painel_feedback" | "chat";
  };
}

// Usuário envia arquivo para Jess
export interface EnviarArquivoParaJessEvent {
  type: "enviar_arquivo_para_jess";
  timestamp: string;
  data: {
    chatId: string;
    usuarioId: string;
    mensagem?: string;
    arquivo: {
      nome: string;
      url: string;
      tipo: string;
      tamanhoKB: number;
    };
  };
}

// Confirmação de aprendizado da Jess
export interface ConfirmacaoAprendizadoEvent {
  type: "confirmacao_aprendizado";
  timestamp: string;
  data: {
    chatId: string;
    perguntaId: string;
    texto: string;
  };
}

// Solicitação de painel de feedback
export interface AbrirPainelFeedbackEvent {
  type: "abrir_painel_feedback";
  timestamp: string;
  data: {
    chatId: string;
    perguntaId: string;
    usuarioId: string;
  };
}

// Solicitação de relatório da pergunta
export interface SolicitarRelatorioEvent {
  type: "solicitar_relatorio_pergunta";
  timestamp: string;
  data: {
    perguntaId: string;
  };
}

// Solicitação de atividades da pergunta
export interface SolicitarAtividadesEvent {
  type: "solicitar_atividades_pergunta";
  timestamp: string;
  data: {
    perguntaId: string;
  };
}

// Atualização visual da pergunta respondida
export interface StatusRespostaJessEvent {
  type: "status_resposta_jess";
  timestamp: string;
  data: {
    perguntaId: string;
    chatId: string;
    respondidoPor: string;
    nomeUsuario: string;
    resposta: string;
    exibirBotoes: {
      verDetalhes: boolean;
      verSugestao: boolean;
    };
  };
}

// Solicitação de modal com sugestão respondida
export interface SolicitarSugestaoEvent {
  type: "solicitar_sugestao_respondida";
  timestamp: string;
  data: {
    perguntaId: string;
  };
}

// Heartbeat
export interface HeartbeatEvent {
  type: "heartbeat";
  timestamp: string;
}

// ============= CHAT HISTORY TYPES =============

// Request type for chat connected
export interface ChatConnectedRequest {
  clientID: string;
  clientType: "staff" | "user";
}

// Chat item types
export enum ChatItemType {
  Message = "message",
  HumanRequest = "humanRequest",
}

// Message from history - matches Go struct with json tags
export interface Message {
  id: string;
  clientID: string;
  timestamp: number;
  body: string;
  message: string;
  from: string;
  fromMe: boolean;
  hasMedia: boolean;
  notifyName: string;
  channel: string;
  session: string;
  shouldReply: boolean;
  who: "user" | "assistant"; // Sender type - "user" or "assistant"
  media?: {
    url: string;
    base64Data?: string;
    mimetype: string;
    filename: string;
    filesize: number;
    metadata?: string;
  };
  vCard?: any;
  location?: any;
}

// Human request info
export interface HumanRequestInfo {
  human_qa_id: number;
  created_at: string; // ISO string from server, converted to Date when needed
  message: string;
  team_messages: Message[];
  isCompleted: boolean;
  humanSupportType: HumanSupportType;
  stepId: string;
}

// Chat item wrapper
export interface ChatItem {
  id: string;
  type: ChatItemType;
  payload: Message | HumanRequestInfo;
  created_at: string; // ISO string from server, converted to Date when needed
}

// Chat history event
export interface ChatHistoryEvent {
  type: "chatHistory";
  timestamp: string;
  data: {
    items: ChatItem[];
  };
}

// ============= HUMAN QA ANSWER TYPES =============

// MediaType - matches Go struct with json tags
export interface MediaType {
  url: string;
  base64Data?: string;
  mimetype: string;
  filename?: string;
  filesize?: number;
}

// HumanSupportMessage - matches Go struct with json tags.
// Backend contract: submit-human-QA payload sends humanSupportMessage[] with
// media = agent's question attachment (single), answerMedia = user's answer
// attachments (array). Backend should accept answerMedia as an array.
export interface HumanSupportMessage {
  question: string;
  answer: string;
  media?: MediaType; // Agent's question media
  answerMedia?: MediaType[]; // User's answer media
}

// HumanSupport - matches Go struct with json tags
export interface HumanSupport {
  isRequired: boolean;
  isCompleted: boolean;
  humanSupportType: HumanSupportType;
  humanSupportMessage?: HumanSupportMessage[];
  humanQaId: string;
  createdAt: string; // ISO string from server
}

// HumanSupportDTO - matches Go struct with json tags
export interface HumanSupportDTO {
  humanSupport: HumanSupport;
  messageId: string;
  stepId: string;
  clientId: string; // ID of the client that this request is for
}

// ============= AGENT MESSAGE BASE TYPE =============

// Request type constants (client -> server)
export const REQUEST_TYPES = {
  TEAM_MESSAGE: "teamMessage",
  USER_MESSAGE: "userMessage",
  CHAT_CONNECTED: "chatConnected",
  SUBMIT_HUMAN_QA_ANSWER: "submitHumanQAAnswer",
  HUMAN_SUPPORT: "requestHumanSupport",
} as const;

// Response type constants (server -> client)
export const RESPONSE_TYPES = {
  MESSAGE_RESPONSE: "messageResponse",
  CHAT_HISTORY: "chatHistory",
  REQUEST_HUMAN_SUPPORT: "requestHumanSupport",
  HUMAN_QA_TEAM_MESSAGE_UPDATE: "humanQATeamMessageUpdate",
  KPI_DASHBOARD_UPDATE: "kpiDashboardUpdate",
} as const;

export type RequestType = (typeof REQUEST_TYPES)[keyof typeof REQUEST_TYPES];
export type ResponseType = (typeof RESPONSE_TYPES)[keyof typeof RESPONSE_TYPES];

// Base AgentMessage structure that all WebSocket messages must follow
export interface AgentMessage<T = any> {
  requestType: RequestType | ResponseType;
  createdAt: string; // ISO 8601 timestamp
  payload: T;
}

// Backend response structure
export interface BackendAgentMessage<T = any> {
  id?: string;
  requestType: ResponseType;
  createdAt: string;
  payload: T;
}

// ============= WEBSOCKET MESSAGE PAYLOADS =============

// SendMessageToService - Backend payload for messageResponse
export interface SendMessageToService {
  clientID: string;
  messageID: string;
  answeringTo: boolean; // deprecated
  chatId: string;
  originsIA: boolean;
  channel: string;
  hasMedia: boolean;
  session: string;
  shouldReply: boolean;
  shouldConvertToSpeech?: boolean;
  language?: string;
  media?: MediaType[];
  data?: Data;
}

// Data field for SendMessageToService
export interface Data {
  body?: string; // For backward compatibility
  message?: string; // New messageResponse format
  timestamp?: string; // Message timestamp
  feedback?: {
    question: string;
    answer?: string;
    media?: string;
  };
}

// WebSocket message structure for team messages (Message type from backend)
export interface WebSocketMessage {
  id: string;
  clientID: string;
  timestamp: number; // Unix timestamp
  body: string;
  from: string;
  fromMe: boolean;
  hasMedia: boolean;
  media?: {
    url: string;
    base64Data?: string;
    mimetype: string;
    filename: string;
    filesize: number;
  };
  notifyName: string;
  channel: string;
  session: string;
  shouldReply: boolean;
  vCard?: any; // ContactCard type if needed
  location?: any; // Location type if needed
}

// Chat connected payload - already correct
export interface ChatConnectedPayload {
  clientID: string;
  clientType: "staff" | "user";
}

// ============= AGENT MESSAGE TYPES =============

// Team message using AgentMessage base
export type TeamMessage = AgentMessage<WebSocketMessage>;

// User message using AgentMessage base
export type UserMessage = AgentMessage<WebSocketMessage>;

// Chat connected message using AgentMessage base
export type ChatConnectedMessage = AgentMessage<ChatConnectedPayload>;

// Human QA answer message using AgentMessage base
export type HumanQAAnswerMessage = AgentMessage<HumanSupportDTO>;

// ============= KPI DASHBOARD UPDATE =============

export interface KpiDashboardUpdateEvent {
  type: "kpiDashboardUpdate";
  timestamp: string;
  data: any; // KpiDashboardPayload from backend (categories, lastUpdatedAt, summary, agentName)
}

// ============= UNIÃO DE TODOS OS EVENTOS =============

// Union type para todos os eventos possíveis
export type ChatJessWebSocketEvent =
  | MessageFromJessEvent
  | RespostaParaJessEvent
  | EnviarArquivoParaJessEvent
  | ConfirmacaoAprendizadoEvent
  | AbrirPainelFeedbackEvent
  | SolicitarRelatorioEvent
  | SolicitarAtividadesEvent
  | StatusRespostaJessEvent
  | SolicitarSugestaoEvent
  | HeartbeatEvent
  | ChatHistoryEvent
  | KpiDashboardUpdateEvent;

// ============= INTERFACES DE SUPORTE =============

export interface ChatJessConnectionStatus {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  lastPing?: Date;
}

// Configuração do WebSocket
export interface ChatJessWebSocketConfig {
  url: string;
  reconnectAttempts: number;
  reconnectDelay: number;
  heartbeatInterval: number;
}

// Estado do chat
export interface ChatJessState {
  messages: ChatJessMessage[];
  isTyping: boolean;
  connection: ChatJessConnectionStatus;
  selectedFeedback: ChatJessMessage | null;
  isWaitingForResponse: boolean;
}

// Interface para notificações do sistema
export interface SystemNotification {
  title: string;
  message: string;
  type: "info" | "warning" | "error" | "success";
  autoClose?: number;
}
